// ================= CLOUD DECK SYNC =================
//
// Carries a player's saved decks between their devices. See
// deploy_handoff/accounts-stage1-scope.md for the whole argument; the two things worth knowing
// before reading the code are:
//
//   LOCAL-FIRST, ALWAYS. localStorage stays the thing the game reads. js/deck-store.js is
//   synchronous and js/main.js calls mergeIntoGame() before the setup screen renders, so the deck
//   list MUST exist before any network call could have finished. Sync therefore only pushes to and
//   pulls into local storage; it is never in the read path. Offline and file:// keep working by
//   construction rather than by care, because nothing on the read path changed at all.
//
//   THE MERGE IS A PURE FUNCTION. `plan()` takes four plain objects and returns a list of actions.
//   It touches no network, no storage and no clock. That is the whole reason the interesting rules
//   are testable here: every suite in this repo is jsdom, so a rule that lives in the client is
//   covered and the same rule in a Worker is not.
//
// NO BUILD FLAG, which departs from §7 of the scope doc and is worth saying why. The doc called for
// one so a file:// build would not offer a button that cannot work. But /api/me already answers
// `configured`, and file:// is detectable directly -- so the feature hides itself from the two
// conditions that matter without a fourth thing for tools/build-dist.js to write correctly. A flag
// that must be kept in step with PROFILES is a way to ship a build whose flags are wrong.
(function () {
  'use strict';

  // What we knew at the end of the last sync. `seen[id]` is that deck's updatedAt AS OF THEN, which
  // is the merge base: it is what makes "changed on one side" distinguishable from "changed on
  // both", and without it every difference looks like a conflict.
  const STATE_KEY = 'sc.deckSync.v1';
  const COPY_SUFFIX = ' (from another device)';

  const R = () => window.DeckRules;
  const Store = () => window.DeckStore;

  function readState() {
    try {
      const raw = window.localStorage.getItem(STATE_KEY);
      const p = raw ? JSON.parse(raw) : null;
      if (!p || typeof p !== 'object') return { playerId: null, seen: {}, syncedAt: null };
      return {
        playerId: typeof p.playerId === 'string' ? p.playerId : null,
        seen: (p.seen && typeof p.seen === 'object' && !Array.isArray(p.seen)) ? p.seen : {},
        syncedAt: typeof p.syncedAt === 'string' ? p.syncedAt : null
      };
    } catch (e) { return { playerId: null, seen: {}, syncedAt: null }; }
  }

  function writeState(s) {
    try { window.localStorage.setItem(STATE_KEY, JSON.stringify(s)); return true; }
    catch (e) { return false; }
  }

  // ---------------- the merge ----------------------------------------------------------------
  //
  // THE RULE THIS ALL SERVES, taken from the one js/deck-store.js already lives by: a deck the
  // player built is never silently destroyed. Locally that means a deck which no longer validates
  // is kept and flagged rather than repaired or deleted. Across devices it means an edit is never
  // discarded to resolve a conflict -- if both sides moved, BOTH survive, and the player decides.
  //
  //   local       { id: record }        every deck in local storage, with its updatedAt
  //   tombstones  { id: deletedAt }     decks deleted locally since the last sync
  //   server      [ {deckId, code, updatedAt, deletedAt} ]
  //   seen        { id: updatedAt }     the merge base, from the end of the last sync
  //
  // ISO-8601 UTC strings compare lexicographically in the same order they compare chronologically,
  // which is the only reason `>` is honest on a timestamp here. Both sides produce them with
  // toISOString(); anything writing a local-time string would break this quietly.
  function plan(input) {
    const local = input.local || {};
    const tombstones = input.tombstones || {};
    const server = input.server || [];
    const seen = input.seen || {};
    const pullOnly = !!input.pullOnly;

    const byId = {};
    server.forEach(row => { if (row && row.deckId) byId[row.deckId] = row; });

    const out = { adopt: [], addAsCopy: [], push: [], pushDelete: [], removeLocal: [], forget: [] };
    const ids = {};
    Object.keys(local).forEach(id => { ids[id] = 1; });
    Object.keys(tombstones).forEach(id => { ids[id] = 1; });
    Object.keys(byId).forEach(id => { ids[id] = 1; });

    Object.keys(ids).forEach(id => {
      const L = local[id];
      const T = tombstones[id];
      const S = byId[id];
      const base = seen[id];

      if (!S) {
        // The server has never heard of this deck.
        if (L && !pullOnly) out.push.push(id);
        // A tombstone for something the server does not have is finished business, not a delete to
        // send: there is nothing there to remove.
        else if (T && !L) out.forget.push(id);
        return;
      }

      if (S.deletedAt) {
        // Deleted elsewhere. A local edit made AFTER that delete resurrects it -- the newer
        // intention wins, and it is the only one that can still be about a deck someone wanted.
        if (L && L.updatedAt > S.updatedAt) { if (!pullOnly) out.push.push(id); }
        else if (L) out.removeLocal.push(id);
        else if (T) out.forget.push(id);
        return;
      }

      if (!L) {
        // Not here. Either it is new to this device, or this device deleted it.
        if (T && T > S.updatedAt) { if (!pullOnly) out.pushDelete.push(id); }
        else {
          out.adopt.push(S);
          // Someone edited it after this device deleted it, so the deck comes back -- and the
          // tombstone must go with it, or it sits in storage forever proposing a delete that the
          // "present on both sides" branch will never look at again.
          if (T) out.forget.push(id);
        }
        return;
      }

      // Present on both sides. `base === undefined` means this pairing has never been reconciled,
      // so neither side can be assumed unchanged -- treated as changed, which routes an unequal
      // pair to keep-both rather than to a silent overwrite.
      const localChanged = base === undefined || L.updatedAt > base;
      const serverChanged = base === undefined || S.updatedAt > base;

      if (localChanged && serverChanged) {
        if (L.updatedAt === S.updatedAt) return;   // same edit, already agreed
        // KEEP BOTH. The local deck keeps its identity and its id, because the player is looking at
        // it; the divergent server copy arrives beside it as a new deck, named so its origin is
        // obvious. Choosing between them would be the network discarding an edit the player made,
        // which is exactly what the store refuses to do locally.
        out.addAsCopy.push(S);
      } else if (serverChanged) {
        out.adopt.push(S);
      } else if (localChanged) {
        if (!pullOnly) out.push.push(id);
      }
    });

    return out;
  }

  // ---------------- record <-> share code ------------------------------------------------------
  //
  // The wire format is the game's own share code, so the server never parses a deck and cannot hold
  // an opinion about what is legal that disagrees with js/deck-rules.js.
  function toCode(record) {
    const res = R().encodeShareCode({
      name: record.name, description: record.description,
      style: record.style, artKey: record.artKey, cards: record.cards
    });
    return res && res.ok ? res.code : null;
  }

  // A PULLED DECK THAT NO LONGER VALIDATES IS STILL STORED. decodeShareCode returns the decoded deck
  // alongside an ILLEGAL verdict, and DeckStore.adopt() writes it without a legality check so
  // DeckStore.inspect() can flag it for repair in the builder's list. Dropping it instead would be
  // the sync deleting a deck the player built because the card pool moved -- the precise failure
  // the store's revalidation rule exists to prevent, arriving by a different route.
  function fromCode(code) {
    const res = R().decodeShareCode(code);
    if (res && res.deck) return res.deck;
    return null;
  }

  // ---------------- network --------------------------------------------------------------------

  function offline() { return window.location && window.location.protocol === 'file:'; }

  async function getJson(url, opts) {
    const res = await window.fetch(url, Object.assign({ credentials: 'same-origin' }, opts || {}));
    // CONTENT-TYPE, NOT STATUS. A deploy that lost its Functions answers /api/ with the site's own
    // HTML, and until this repo shipped a 404.html it did so with status 200 -- so `res.ok` was
    // true and `res.json()` threw a parse error that reads like a server fault. Checked explicitly
    // so the failure says what it is.
    const type = res.headers.get('Content-Type') || '';
    if (!/json/i.test(type)) throw new Error('api_unavailable');
    return { status: res.status, body: await res.json() };
  }

  // NO `if (offline()) return` SHORT-CIRCUIT HERE, and that is a layering decision rather than an
  // oversight. This module reports what it finds; the UI decides not to ask. A hard gate on the
  // protocol would make the whole round trip untestable, because a jsdom window loaded from a file
  // is on file:// -- so the gate would be permanently on in exactly the place the rules are proved.
  // On a real file:// page the fetch below simply fails and is caught, which reaches the same
  // answer by the honest route.
  async function status() {
    try {
      const { body } = await getJson('/api/me');
      return {
        configured: !!body.configured, signedIn: !!body.signedIn,
        playerId: body.playerId || null, offline: false,
        // Passed straight through for the brand bar's chip. This module has no use for either and
        // deliberately does not remember them: they are display, they change on Discord's side, and
        // the only honest copy is whatever the last /api/me said.
        name: body.name || null, avatarUrl: body.avatarUrl || null
      };
    } catch (e) {
      return { configured: false, signedIn: false, playerId: null, offline: true, name: null, avatarUrl: null };
    }
  }

  // ---------------- applying a plan ------------------------------------------------------------

  function recordFrom(deck, id, updatedAt, existing) {
    return {
      schemaVersion: Store().SCHEMA_VERSION,
      id: id,
      name: deck.name, style: deck.style, artKey: deck.artKey,
      description: deck.description, cards: deck.cards.slice(),
      createdAt: (existing && existing.createdAt) || updatedAt,
      updatedAt: updatedAt
    };
  }

  // Returns the ids it created for kept-both copies, rather than leaving the caller to find them by
  // name: two halves that each work out "which deck was the copy" are two halves that can disagree.
  function applyPlan(p, state) {
    const store = Store();
    const map = store._readAll();
    const createdCopies = [];
    let changed = 0;

    p.adopt.forEach(row => {
      const deck = fromCode(row.code);
      if (!deck) return;
      map[row.deckId] = recordFrom(deck, row.deckId, row.updatedAt, map[row.deckId]);
      state.seen[row.deckId] = row.updatedAt;
      changed++;
    });

    p.addAsCopy.forEach(row => {
      const deck = fromCode(row.code);
      if (!deck) return;
      const id = store.newLocalId(map);
      deck.name = (deck.name + COPY_SUFFIX).slice(0, R().NAME_MAX);
      map[id] = recordFrom(deck, id, row.updatedAt, null);
      createdCopies.push(id);
      // The ORIGINAL id takes the server's timestamp as its new base even though the local deck
      // under that id was left alone -- otherwise the same divergence is rediscovered on every
      // later sync and the player collects a copy each time.
      state.seen[row.deckId] = row.updatedAt;
      changed++;
    });

    p.removeLocal.forEach(id => {
      delete map[id];
      delete state.seen[id];
      changed++;
    });

    if (changed) store._writeAll(map);
    p.forget.forEach(id => store.clearTombstone(id));
    return { changed: changed, createdCopies: createdCopies };
  }

  // ---------------- the whole round trip -------------------------------------------------------

  async function sync() {
    const store = Store();
    if (!store || !store.available()) return { ok: false, error: 'no_storage' };

    let who;
    try { who = await status(); } catch (e) { return { ok: false, error: 'api_unavailable' }; }
    if (!who.configured) return { ok: false, error: 'not_configured' };
    if (!who.signedIn) return { ok: false, error: 'not_signed_in' };

    const state = readState();

    // A DIFFERENT ACCOUNT ON THIS DEVICE PULLS ONLY. Local decks are not uploaded into an account
    // that has never seen them: on a shared browser that would put one person's decks into another
    // person's account, which is the one direction of this feature that cannot be undone by the
    // person it happened to. The decks stay on the device, and sync normally again when their own
    // account signs back in. Known limitation, stated rather than hidden: two accounts sharing one
    // browser profile still SEE each other's decks locally, because local storage is per browser
    // and not per player.
    const pullOnly = !!(state.playerId && state.playerId !== who.playerId);
    if (state.playerId !== who.playerId) { state.seen = {}; state.playerId = who.playerId; }

    let pulled;
    try { pulled = await getJson('/api/decks'); }
    catch (e) { return { ok: false, error: 'api_unavailable' }; }
    if (pulled.status === 401) return { ok: false, error: 'not_signed_in' };
    if (pulled.status !== 200) return { ok: false, error: 'server_error' };

    const p = plan({
      local: store._readAll(),
      tombstones: store.tombstones(),
      server: pulled.body.decks || [],
      seen: state.seen,
      pullOnly: pullOnly
    });

    const applied = applyPlan(p, state);
    const changed = applied.changed;

    // ---- push ----
    const map = store._readAll();
    const outgoing = [];
    // The kept-both copies are new decks the server has never seen, so they go up alongside the
    // ordinary pushes and both devices converge on the same two decks.
    p.push.concat(pullOnly ? [] : applied.createdCopies).forEach(id => {
      const rec = map[id];
      if (!rec) return;
      const code = toCode(rec);
      // A deck that cannot be encoded -- it holds a card newer than the frozen share manifest --
      // is skipped rather than failing the sync. It stays local and keeps working; the alternative
      // is one unshareable deck stopping every other deck from ever reaching the account.
      if (code && !outgoing.some(o => o.deckId === id)) {
        outgoing.push({ deckId: id, code: code, updatedAt: rec.updatedAt });
      }
    });
    const deletes = pullOnly ? [] : p.pushDelete.map(id => ({
      deckId: id, code: '', updatedAt: store.tombstones()[id], deletedAt: store.tombstones()[id]
    }));

    const batch = outgoing.concat(deletes);
    if (batch.length) {
      let res;
      try {
        res = await getJson('/api/decks', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ decks: batch })
        });
      } catch (e) { return { ok: false, error: 'api_unavailable', pulled: changed }; }
      if (res.status !== 200) return { ok: false, error: 'server_error', pulled: changed };
      batch.forEach(d => { state.seen[d.deckId] = d.updatedAt; });
      deletes.forEach(d => store.clearTombstone(d.deckId));
    }

    state.syncedAt = new Date().toISOString();
    writeState(state);
    return {
      ok: true, pulled: changed, pushed: batch.length,
      keptBoth: p.addAsCopy.length, syncedAt: state.syncedAt, pullOnly: pullOnly
    };
  }

  // Erase the account and everything the server holds about it. Returns the server's own count of
  // what went, so the UI can report a fact rather than an assumption.
  //
  // LOCAL DECKS ARE DELIBERATELY LEFT ALONE. The player built them, they are in this browser, and
  // deleting them to satisfy a request about the SERVER'S copy would destroy work the feature
  // exists to protect. The sync state goes, because it describes an account that no longer exists.
  async function deleteAccount() {
    let res;
    try {
      res = await getJson('/api/me', {
        method: 'DELETE',
        // Sent explicitly: fetch omits Origin on same-origin requests for some methods, and the
        // endpoint refuses anything it cannot confirm is same-origin.
        headers: { 'X-Requested-With': 'sparring-cards' }
      });
    } catch (e) { return { ok: false, error: 'api_unavailable' }; }
    if (res.status === 401) return { ok: false, error: 'not_signed_in' };
    if (res.status !== 200) return { ok: false, error: 'server_error' };
    writeState({ playerId: null, seen: {}, syncedAt: null });
    return { ok: true, decksRemoved: (res.body && res.body.decksRemoved) || 0 };
  }

  async function signOut() {
    try {
      await window.fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
    } catch (e) { /* signing out of a server we cannot reach still clears this device */ }
    // The merge base belongs to the account that just left. Kept-per-account rather than cleared to
    // nothing would be wrong too: the next sign-in is a different reconciliation.
    writeState({ playerId: null, seen: {}, syncedAt: null });
    return { ok: true };
  }

  window.DeckSync = {
    STATE_KEY: STATE_KEY,
    COPY_SUFFIX: COPY_SUFFIX,
    SIGN_IN_URL: '/api/auth/start',
    offline: offline,
    status: status,
    sync: sync,
    signOut: signOut,
    deleteAccount: deleteAccount,
    // WHAT SIGNING IN ACTUALLY STORES, in one place so the sign-in prompt and the account panel
    // cannot drift into describing different things. Kept beside the code that does the storing
    // rather than in the markup, because it is a claim about behaviour and it has a test.
    // REWRITTEN WHEN THE SCHEMA CHANGED, in the same commit, because a privacy notice that has
    // fallen behind the database is not a stale comment — it is a false statement to the person it
    // was written for. This used to say "No name, no email, no avatar"; two thirds of that stopped
    // being true when migrations/0003_player_identity.sql added a display name and an avatar so a
    // player could see which account they were signed into.
    //
    // Still true and still worth saying: the email is not asked for at all (`identify` is the only
    // scope requested), and everything held can be removed.
    PRIVACY_NOTE: 'Signing in stores your Discord user ID, display name, avatar and your saved ' +
                  'decks, plus the bouts you play, the daily challenges you beat, the credits they ' +
                  'earn and the cards you buy with them. No email — we never ' +
                  'ask for it. You can delete all of it at any time.',
    lastSyncedAt: () => readState().syncedAt,
    // The pure merge, exported because it is the part worth testing and the part a reader should
    // check. Everything above it is plumbing around this.
    plan: plan,
    _readState: readState,
    _writeState: writeState,
    _applyPlan: applyPlan
  };
})();
